/**
 * A single-select row of options. Used for energy, and anything else with a
 * small fixed set.
 *
 * Presentational only: it takes option ids and labels and knows nothing about
 * what they mean (`ui-is-presentational`). Selecting the current value clears
 * it, so correcting a mistake costs the same as making one.
 */
export interface ChoiceOption<T extends string> {
  value: T
  label: string
  glyph?: string
}

export interface ChoiceRowProps<T extends string> {
  legend: string
  options: readonly ChoiceOption<T>[]
  value: T | undefined
  onChange: (value: T | undefined) => void
}

export function ChoiceRow<T extends string>({
  legend,
  options,
  value,
  onChange,
}: ChoiceRowProps<T>) {
  return (
    <fieldset>
      <legend className="pb-2 text-sm font-medium">{legend}</legend>
      <div className="flex gap-2" role="radiogroup" aria-label={legend}>
        {options.map((option) => {
          const selected = value === option.value
          return (
            <button
              key={option.value}
              type="button"
              role="radio"
              aria-checked={selected}
              onClick={() => onChange(selected ? undefined : option.value)}
              className={[
                'flex min-h-11 flex-1 flex-col items-center justify-center gap-0.5',
                'rounded-lg border text-xs transition-all duration-200 active:scale-[0.97]',
                selected
                  ? 'border-accent bg-accent text-accent-ink shadow-sm'
                  : 'border-line hover:bg-line/40 hover:border-ink-muted/30 text-ink',
              ].join(' ')}
            >
              {option.glyph ? (
                <span aria-hidden="true" className="text-sm leading-none">
                  {option.glyph}
                </span>
              ) : null}
              <span>{option.label}</span>
            </button>
          )
        })}
      </div>
    </fieldset>
  )
}
